import { Chunk, FileWithDiffs, MissingSubRowsOptions, FccRow } from "@/models/FCC"
import { extractFullFileLines } from "."

const MAX_ROWS = 20

/**
 * Returns flattened lines array with the main chunks and their closest 20 rows
 * @param {FileWithDiffs} file - The file that needs to get parsed into {@link FccRow}[]
 *
 * Logic is split according to the following steps:
 * 1. Add "up" expander rows (max 20 subrows) before the chunk. Since we want the rows to align to the start of the chunk,
 *    we begin from it and move our way up. The expanders need to then be rendered in the reverse order, hence
 *    the need for `prevRows`
 * 2. Load chunk lines
 * 3. If another chunk exists - add a "middle" expander that expands ALL ROWS between the 2 chunks and skip to 2.
 * 4. Load "down" expander rows (max 20 subrows) after the last chunk and until the end of the file.
 *
 * Note: Deciding whether to render (or not) a specific expander line is handled in
 * {@link ExpanderRow}
 *
 * @returns {FccRow[]} Rows of content, either code or expander rows, to be rendered
 */
export const flattenLines = (file: FileWithDiffs): FccRow[] => {
  const { file_content, chunks } = file
  const flattenedLines: FccRow[] = []
  const fullFileRowsArray: string[] = extractFullFileLines(file_content)

  // go over the chunks and add the lines, before_lines and after_lines to the flattenedLines array
  for (let i = 0; i < chunks.length; i++) {
    const isFirstChunk = i === 0
    const isLastChunk = i === chunks.length - 1
    const currentChunk = chunks[i]
    const nextChunk = chunks[i + 1]

    const currentChunkFirstLine = getChunkLineNumber(currentChunk, "up")
    const currentChunkLastLine = getChunkLineNumber(currentChunk, "down")
    const nextChunkFirstLine = getChunkLineNumber(nextChunk || null, "up")

    if (isFirstChunk && currentChunkFirstLine && currentChunkFirstLine > 1) {
      // Step 1 - create expander rows before first chunk
      const preRows: MissingSubRowsOptions[] = []
      for (let index = currentChunkFirstLine - 1; index >= 0; index = index - MAX_ROWS) {
        const prevIndex = Math.max(0, index - MAX_ROWS)
        preRows.push({
          flattenedLines,
          subRows: fullFileRowsArray.slice(prevIndex, index),
          fromIndex: prevIndex,
          expanderDirection: "up",
          nextLineNumber: index
        })
      }
      preRows.reverse()
      preRows.forEach((row) => createExpanderRow(row))
    }

    // Step 2 - adding the chunk current lines, before and after
    flattenedLines.push(...currentChunk.before_lines, ...currentChunk.lines, ...currentChunk.after_lines)

    if (nextChunkFirstLine && currentChunkLastLine && nextChunkFirstLine - currentChunkLastLine > 1) {
      // Step 3 - adding a middle expander
      middleChunkSubRowsProcessLogic(flattenedLines, nextChunkFirstLine, currentChunkLastLine, fullFileRowsArray)
    }

    // Step 5 - adding last sub rows
    if (currentChunkLastLine && file.total_file_lines > currentChunkLastLine + 1 && isLastChunk) {
      for (let index = currentChunkLastLine; index < file.total_file_lines; index += MAX_ROWS) {
        const nextIndex = Math.min(index + MAX_ROWS, file.total_file_lines)

        createExpanderRow({
          flattenedLines,
          subRows: fullFileRowsArray.slice(index, nextIndex),
          fromIndex: index,
          expanderDirection: "down",
          nextLineNumber: nextIndex
        })
      }
    }
  }

  return removeDuplicatedLines(flattenedLines)
}

const middleChunkSubRowsProcessLogic = (
  flattenedLines: FccRow[],
  nextChunkFirstLine: number,
  currentChunkLastLine: number,
  extractedFullFile: string[] | null
) => {
  const index = currentChunkLastLine
  const nextIndex = nextChunkFirstLine - 1

  createExpanderRow({
    flattenedLines,
    subRows: extractedFullFile?.slice(index, nextIndex) || null,
    fromIndex: index,
    expanderDirection: "middle",
    nextLineNumber: nextChunkFirstLine
  })
}

export const createExpanderRow = ({
  flattenedLines,
  subRows,
  fromIndex,
  expanderDirection,
  nextLineNumber
}: MissingSubRowsOptions) => {
  let calculatedIndex = 0

  if (typeof fromIndex === "number") {
    calculatedIndex = Math.floor(fromIndex)
  }

  const subRowsToBeAdded =
    subRows?.map((row, index) => ({
      new_line_number: index + calculatedIndex + 1,
      new_content: row,
      isExpanded: false
    })) || []

  // TODO: extract the expander type to it's separate type
  flattenedLines.push({
    ...flattenedLines[calculatedIndex],
    subRows: subRowsToBeAdded,
    arrowDirection: expanderDirection,
    startLineNumber: fromIndex,
    nextLineNumber,
    number: null,
    new_line_number: null
  })
}

const getChunkLineNumber = (chunk: Chunk | null, direction: "up" | "down"): number | null =>
  chunk
    ? direction === "up"
      ? (chunk.before_lines[0]?.new_line_number ?? chunk.before_lines[0]?.number)
      : (chunk.after_lines[chunk.after_lines.length - 1]?.new_line_number ??
        chunk.after_lines[chunk.after_lines.length - 1]?.number)
    : null

const removeDuplicatedLines = (lines: FccRow[]): FccRow[] => {
  const linesMap = new Set<string>()

  return lines.filter((line) => {
    if (!line.new_line_number) {
      return true // Keep lines without new_line_number
    }

    const lineNumber = line.new_line_number.toString()
    if (linesMap.has(lineNumber)) {
      return false // Skip duplicates
    }

    linesMap.add(lineNumber)
    return true
  })
}
